import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, RotateCw, Pencil, Check, RefreshCw, Monitor, Smartphone, Square, Grid3X3, Settings, Wrench, MapPin, Clock, ChevronDown, Play, Calendar, Image as ImageIcon, Video } from 'lucide-react';
import StatusBadge from './StatusBadge.jsx';
import {
  getCmsSchedules,
  getContentStatus,
  getDeviceScreenRotation,
  getScreenRotation,
  putDeviceScreenRotation,
  putScreenRotation,
} from '../api/contentApi';
import { updateDevice, applyDeviceProfile } from '../api/tvApi';
import ProfilePickerModal from './ProfilePickerModal.jsx';
import HardwareChangeCard from './HardwareChangeCard.jsx';
import DisplayHealthCard from './DisplayHealthCard.jsx';
import DiagnosticsPanel from './DiagnosticsPanel.jsx';
import CalibrationForm from './CalibrationForm.jsx';
import { getActiveProjectId } from '../constants/projectConstants';
import { getDeviceDisplayName, getLastSeen } from '../utils/deviceLabel';

// Keep in sync with ProfilePickerModal's icon map — both consume the same
// icon strings from the backend SYSTEM_PROFILES seed.
const PROFILE_ICON_MAP = {
  monitor: Monitor,
  smartphone: Smartphone,
  square: Square,
  grid: Grid3X3,
  'grid-3x3': Grid3X3,
  settings: Settings,
};
const ProfileIcon = ({ name, size = 18 }) => {
  const Comp = PROFILE_ICON_MAP[name] || Monitor;
  return <Comp size={size} />;
};

const ROTATION_OPTIONS = [0, 90, 180, 270];

/** Read degrees from common API shapes: flat fields, snake_case, or `{ success, data }`. */
const extractRotationDegrees = (raw) => {
  if (raw == null) return null;
  if (typeof raw === 'number' && ROTATION_OPTIONS.includes(raw)) return raw;
  const tryNum = (v) => {
    const n = Number(v);
    return ROTATION_OPTIONS.includes(n) ? n : null;
  };
  const bucket = raw && typeof raw === 'object' ? raw : {};
  const inner = bucket.success === true && bucket.data != null ? bucket.data : bucket;
  const nested = inner && typeof inner === 'object' ? inner : {};
  const candidates = [
    nested.screenRotationDegrees,
    nested.screen_rotation_degrees,
    nested.rotationDegrees,
    nested.rotation,
    bucket.screenRotationDegrees,
    bucket.screen_rotation_degrees,
    bucket.rotationDegrees,
  ];
  for (const c of candidates) {
    const n = tryNum(c);
    if (n != null) return n;
  }
  return null;
};

const parseRotationResponse = (data) => extractRotationDegrees(data) ?? 0;

/** When `default`, backend could not resolve a valid angle from assignment + cms row (e.g. id mismatch); dashboard then prefers `screen_rotation_degrees` from the list row. See `docs/SUMADHURA_SCHEDULER_DASHBOARD_INTEGRATION.md`. */
const getRotationSourceFromApiPayload = (data) => {
  if (data == null || typeof data !== 'object') return null;
  const inner = data.success === true && data.data != null ? data.data : data;
  return inner.rotationSource ?? inner.rotation_source ?? data.rotationSource ?? data.rotation_source ?? null;
};

/** Merge GET result with cms-devices list when `rotationSource === 'default'` so UI matches Mongo until ids align with backend resolution. */
const resolveRotationDegrees = (apiPayload, deviceRow) => {
  const fromApi = extractRotationDegrees(apiPayload);
  const fromList = extractRotationDegrees(deviceRow);
  const src = getRotationSourceFromApiPayload(apiPayload);
  if (src === 'default' && fromList != null) return fromList;
  if (fromApi != null) return fromApi;
  if (fromList != null) return fromList;
  return 0;
};

/**
 * TVDetails Component
 * Displays detailed information about the selected TV
 */
const TVDetails = ({ tv, onClose, onDeviceUpdated, hardwareChangeBlastRadius = null, knownLocations = [] }) => {
  const [rotation, setRotation] = useState(0);
  const [rotationLoading, setRotationLoading] = useState(false);
  const [rotationSaving, setRotationSaving] = useState(false);
  const [rotationError, setRotationError] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [schedulesError, setSchedulesError] = useState(null);
  // contentId/name/nanoid → human title. Schedule rows arrive with only
  // content_id (nanoid or filename) — no title — so the drawer would render
  // the raw id (e.g. "iAa12RzMez") without this lookup.
  const [contentTitleByKey, setContentTitleByKey] = useState({});
  // Rename flow — lets the admin give this device a friendly name
  const [nameEditing, setNameEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState(null);
  // Local override so the panel instantly reflects a saved rename even if
  // the parent hasn't refreshed its tvs list yet.
  const [localDisplayName, setLocalDisplayName] = useState(null);

  // Track which device we last auto-prompted for setup, so a user who
  // deliberately closed the picker on an unprofiled device doesn't get
  // nagged the moment their click handler re-rendered the same TVDetails
  // panel. We only auto-open once per deviceId per session.
  const [autoPromptedDeviceId, setAutoPromptedDeviceId] = useState(null);

  // Reset local rename state whenever a different TV is selected
  useEffect(() => {
    setNameEditing(false);
    setNameDraft('');
    setNameError(null);
    setLocalDisplayName(null);
  }, [tv?.deviceId, tv?.tvId]);

  // ── Screen profile state ──────────────────────────────────────────────
  // Backend serializer now inlines `profile` + `profileContent` on every
  // device response (see cms.device.controller.js serializeDevice), so
  // we don't need a separate listProfiles() fetch here — everything we
  // render comes from the tv prop directly. `profileListLoaded` is kept
  // as a derived flag (always true) so the auto-prompt effect below
  // doesn't need to wait on a fetch.
  const [calibrationOpen, setCalibrationOpen] = useState(false);
  const [profilePickerOpen, setProfilePickerOpen] = useState(false);
  const [profileApplying, setProfileApplying] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const profileListLoaded = true;

  // Active profile — read from the device serializer response. Carries
  // name + icon + physicalType + hdmiExpected. For content dims the
  // activeProfile object also needs content.* (width/height/aspectRatio)
  // which isn't in serializeDevice's profile subdoc, so we stitch it
  // together from profileContent here for render convenience.
  const activeProfile = useMemo(() => {
    if (!tv?.profile) return null;
    return {
      ...tv.profile,
      content: tv.profileContent || null,
    };
  }, [tv?.profile, tv?.profileContent]);

  // First-run auto-prompt: when the user selects an online, non-pending
  // device that has no profile yet, automatically open the picker. This
  // is the "welcome / setup moment" product experience — feels like a
  // dedicated setup flow without maintaining a separate component.
  // Only fires once per device per session (see autoPromptedDeviceId).
  // A device needs setup if it's missing profile, name, or valid location.
  const needsSetup = !tv?.profileId
    || !tv?.displayName
    || (knownLocations.length > 0 && !knownLocations.includes(tv?.location));

  useEffect(() => {
    if (!tv?.deviceId) return;
    if (!profileListLoaded) return;
    if (!needsSetup) return;               // fully configured
    if (autoPromptedDeviceId === tv.deviceId) return;
    setAutoPromptedDeviceId(tv.deviceId);
    setProfilePickerOpen(true);
  }, [tv?.deviceId, needsSetup, profileListLoaded, autoPromptedDeviceId]);

  const handleApplyProfile = useCallback(async (profileId, _profile, meta) => {
    if (!tv?.deviceId) {
      throw new Error('No deviceId — cannot apply profile');
    }
    setProfileApplying(true);
    setProfileError(null);
    try {
      // If the setup modal provided a name or location, patch them first
      if (meta?.displayName || meta?.location) {
        await updateDevice(tv.deviceId, {
          ...(meta.displayName ? { displayName: meta.displayName } : {}),
          ...(meta.location ? { location: meta.location } : {}),
        });
      }
      const res = await applyDeviceProfile(tv.deviceId, profileId);
      if (typeof onDeviceUpdated === 'function') {
        try { onDeviceUpdated(res?.device || null); } catch (_) { /* noop */ }
      }
    } catch (err) {
      setProfileError(err?.message || 'Apply failed');
      throw err;
    } finally {
      setProfileApplying(false);
    }
  }, [tv?.deviceId, onDeviceUpdated]);

  const effectiveTv = localDisplayName != null
    ? { ...tv, displayName: localDisplayName }
    : tv;
  const currentDisplayName = getDeviceDisplayName(effectiveTv);

  const startRenaming = () => {
    // Seed with the raw displayName (not the derived label) so an admin
    // starting from scratch sees an empty field rather than the fallback text.
    setNameDraft(effectiveTv?.displayName || '');
    setNameError(null);
    setNameEditing(true);
  };

  const cancelRenaming = () => {
    if (nameSaving) return;
    setNameEditing(false);
    setNameDraft('');
    setNameError(null);
  };

  const saveName = async () => {
    const trimmed = (nameDraft || '').trim();
    if (trimmed.length > 80) {
      setNameError('Name must be 80 characters or fewer.');
      return;
    }
    if (!tv?.deviceId) {
      setNameError('This device has no deviceId — cannot rename.');
      return;
    }
    setNameSaving(true);
    setNameError(null);
    try {
      const res = await updateDevice(tv.deviceId, { displayName: trimmed });
      const saved = res?.device?.displayName ?? trimmed ?? null;
      setLocalDisplayName(saved || '');
      setNameEditing(false);
      setNameDraft('');
      if (typeof onDeviceUpdated === 'function') {
        // Give the parent a chance to refresh its device list
        try { onDeviceUpdated(res?.device || null); } catch (_) { /* noop */ }
      }
    } catch (err) {
      setNameError(err?.message || 'Rename failed.');
    } finally {
      setNameSaving(false);
    }
  };

  /** Physical device id (Android/CMS) — when set, prefer GET/PUT `/device-screen/rotation`. */
  const deviceId = tv ? String(tv.deviceId || '').trim() : '';
  /** Logical screen / tvId (e.g. TV_MAIN). Empty when unassigned — rotation APIs use deviceId only. */
  const assignedScreenId = tv && tv.tvId ? String(tv.tvId).trim() : '';
  /** Legacy GET /screens/.../rotation path when there is no cms deviceId. */
  const screenIdForLegacy = tv ? String(tv.tvId || tv.deviceId || '').trim() : '';
  /** CMS schedules API screen filter — same id as Screens table / Schedules view. */
  const scheduleScreenKey = tv ? String(tv.deviceId || tv.tvId || '').trim() : '';

  const loadRotation = useCallback(async () => {
    if (!tv || (!deviceId && !screenIdForLegacy)) return;
    setRotationLoading(true);
    setRotationError(null);
    const seed = extractRotationDegrees(tv);
    if (seed != null) setRotation(seed);
    try {
      let data;
      if (deviceId) {
        data = await getDeviceScreenRotation({
          deviceId,
          screenId: assignedScreenId || null,
          projectId: getActiveProjectId(),
        });
      } else {
        data = await getScreenRotation(getActiveProjectId(), screenIdForLegacy);
      }
      setRotation(resolveRotationDegrees(data, tv));
    } catch (err) {
      setRotationError(err instanceof Error ? err.message : 'Could not load rotation');
      setRotation(extractRotationDegrees(tv) ?? 0);
    } finally {
      setRotationLoading(false);
    }
  }, [tv, deviceId, screenIdForLegacy, assignedScreenId]);

  useEffect(() => {
    if (!tv || (!deviceId && !screenIdForLegacy)) {
      setRotation(0);
      setRotationError(null);
      return;
    }
    const seed = extractRotationDegrees(tv);
    if (seed != null) setRotation(seed);
    loadRotation();
  }, [tv, deviceId, screenIdForLegacy, loadRotation]);

  useEffect(() => {
    if (!tv || !scheduleScreenKey) {
      setSchedules([]);
      setSchedulesError(null);
      return;
    }
    let cancelled = false;
    setSchedulesLoading(true);
    setSchedulesError(null);
    getCmsSchedules({
      projectId: getActiveProjectId(),
      screenId: scheduleScreenKey,
      includeCancelled: false,
    })
      .then((response) => {
        if (cancelled) return;
        const list =
          response?.data && Array.isArray(response.data)
            ? response.data
            : Array.isArray(response)
              ? response
              : [];
        const hasScreenField = list.some(
          (s) =>
            s.screenId != null ||
            s.deviceId != null ||
            s.screen_id != null ||
            s.device_id != null,
        );
        const forScreen = hasScreenField
          ? list.filter(
              (s) =>
                String(s.screenId ?? s.deviceId ?? s.screen_id ?? s.device_id ?? '').trim() ===
                scheduleScreenKey,
            )
          : list;
        setSchedules(forScreen);
      })
      .catch((err) => {
        if (!cancelled) {
          setSchedulesError(err instanceof Error ? err.message : 'Could not load schedules');
          setSchedules([]);
        }
      })
      .finally(() => {
        if (!cancelled) setSchedulesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tv, scheduleScreenKey]);

  // Load the title map once the drawer opens for a screen. Indexed under
  // every identifier the schedule rows may carry (contentId, contentNanoId,
  // content_id, name) so the SCHEDULE list can render "Key New_1080X1920"
  // instead of the raw nanoid.
  useEffect(() => {
    if (!tv) return undefined;
    let cancelled = false;
    getContentStatus(getActiveProjectId())
      .then((res) => {
        if (cancelled) return;
        const map = {};
        for (const c of (res?.content || [])) {
          const title = c.title || c.name;
          if (!title) continue;
          for (const k of [c.contentId, c.contentNanoId, c.name].filter(Boolean)) {
            map[k] = title;
          }
        }
        setContentTitleByKey(map);
      })
      .catch((err) => {
        if (!cancelled) console.warn('content title map load failed:', err?.message);
      });
    return () => { cancelled = true; };
  }, [tv]);

  const handleSetRotation = async (degrees) => {
    if ((!deviceId && !screenIdForLegacy) || rotationSaving) return;
    setRotationSaving(true);
    setRotationError(null);
    try {
      if (deviceId) {
        await putDeviceScreenRotation({
          deviceId,
          screenId: assignedScreenId || undefined,
          rotationDegrees: degrees,
          projectId: getActiveProjectId(),
        });
      } else {
        await putScreenRotation(getActiveProjectId(), screenIdForLegacy, degrees);
      }
      setRotation(degrees);
    } catch (err) {
      setRotationError(err instanceof Error ? err.message : 'Failed to save rotation');
    } finally {
      setRotationSaving(false);
    }
  };

  if (!tv) return null;

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never seen';
    const t = new Date(timestamp).getTime();
    if (!Number.isFinite(t)) return 'Never seen';
    const diff = Date.now() - t;
    if (diff < 0) return 'Just now';
    const sec = Math.floor(diff / 1000);
    if (sec < 45) return 'Just now';
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day === 1) return 'Yesterday';
    if (day < 7) return `${day}d ago`;
    return new Date(timestamp).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' });
  };

  const formatScheduleDateRange = (row) => {
    const d = row?.date || row?.startDate;
    const e = row?.endDate;
    if (d && e && String(d).slice(0, 10) !== String(e).slice(0, 10)) {
      return `${String(d).slice(0, 10)} → ${String(e).slice(0, 10)}`;
    }
    if (d) return String(d).slice(0, 10);
    return 'Daily';
  };

  const s = detailStyles;

  return (
    <div style={s.container}>
      {/* ═══════════════════════════════════════════════════════════
          HERO — Name, location, status, last seen. One glance.
          ═══════════════════════════════════════════════════════════ */}
      <div style={s.hero}>
        <div style={s.heroTop}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {nameEditing ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="text" value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') cancelRenaming(); }}
                  placeholder="Screen name" maxLength={80} autoFocus disabled={nameSaving}
                  style={s.nameInput}
                />
                <button type="button" onClick={saveName} disabled={nameSaving} style={s.nameBtn}>
                  <Check size={14} />
                </button>
                <button type="button" onClick={cancelRenaming} disabled={nameSaving} style={s.nameCancelBtn}>
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div style={s.heroName} onClick={startRenaming} title="Click to rename">
                {currentDisplayName}
                <Pencil size={12} style={{ color: '#94a3b8', flexShrink: 0, opacity: 0.5 }} />
              </div>
            )}
            {nameError && <div style={s.nameError}>{nameError}</div>}
          </div>
          {onClose && (
            <button onClick={onClose} style={s.closeBtn} aria-label="Close">
              <X size={18} />
            </button>
          )}
        </div>

        <div style={s.heroMeta}>
          {tv.location && (
            <span style={s.metaItem}><MapPin size={12} /> {tv.location}</span>
          )}
          <span style={s.metaItem}>
            <span style={{ ...s.statusDot, background: tv.status === 'online' ? '#10b981' : '#ef4444' }} />
            {tv.status === 'online' ? 'Online' : 'Offline'}
          </span>
          <span style={s.metaItem}>
            <Clock size={12} /> {formatTimestamp(getLastSeen(tv))}
          </span>
        </div>

        {/* Profile badge — compact inline */}
        {activeProfile && (
          <div style={s.profileBadge}>
            <ProfileIcon name={activeProfile.icon} size={14} />
            <span>{activeProfile.name}</span>
            <span style={s.profileDims}>
              {activeProfile.content.width}×{activeProfile.content.height}
            </span>
            <button type="button" onClick={() => { setProfileError(null); setProfilePickerOpen(true); }} style={s.profileChangeBtn}>
              Change
            </button>
          </div>
        )}
      </div>

      {/* Hardware change alert — right under hero if present */}
      {tv?.previousEdidFingerprint?.width && (
        <HardwareChangeCard
          device={tv}
          blastRadius={hardwareChangeBlastRadius}
          onOpenProfilePicker={() => { setProfileError(null); setProfilePickerOpen(true); }}
          onDeviceUpdated={onDeviceUpdated}
        />
      )}

      {/* ═══════════════════════════════════════════════════════════
          NOW PLAYING — What's on screen right now
          ═══════════════════════════════════════════════════════════ */}
      <div style={s.section}>
        <div style={s.sectionLabel}><Play size={13} /> Now playing</div>
        {tv.currentContent ? (
          <div style={s.nowPlayingCard}>
            <div style={s.nowPlayingIcon}>
              {tv.currentContent.type === 'video' ? <Video size={16} /> : <ImageIcon size={16} />}
            </div>
            <div>
              <div style={s.nowPlayingTitle}>{tv.currentContent.title || tv.currentContent.type}</div>
              <div style={s.nowPlayingMeta}>{tv.currentContent.type}</div>
            </div>
          </div>
        ) : (
          <div style={s.emptyState}>No content playing</div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════
          SCHEDULE — What's coming up
          ═══════════════════════════════════════════════════════════ */}
      <div style={s.section}>
        <div style={s.sectionLabel}><Calendar size={13} /> Schedule</div>
        {schedulesLoading ? (
          <div style={s.emptyState}>Loading…</div>
        ) : schedulesError ? (
          <div style={{ fontSize: 13, color: '#b91c1c' }}>{schedulesError}</div>
        ) : schedules.length === 0 ? (
          <div style={s.emptyState}>No active schedules</div>
        ) : (
          <div style={s.scheduleList}>
            {schedules.slice(0, 8).map((row) => {
              const sid = row.scheduleId || row.id || `${row.contentId}-${row.startTime}`;
              const loopItems = Array.isArray(row.loopItems) ? row.loopItems
                : Array.isArray(row.loop_items) ? row.loop_items : [];
              const resolveTitle = (key) => key && contentTitleByKey[key];
              const primaryKey = row.contentId || row.content_id;
              let label = row.contentTitle || resolveTitle(primaryKey);
              if (!label && loopItems.length) {
                const names = loopItems
                  .map((li) => resolveTitle(li.contentId || li.content_id) || li.contentId || li.content_id)
                  .filter(Boolean);
                if (names.length) label = names.join(' · ');
              }
              if (!label) label = primaryKey || '—';
              const startT = row.startTime ?? row.start_time ?? '—';
              const endT = row.endTime ?? row.end_time ?? '—';
              const loopOn = row.loopEnabled === true || row.loop_enabled === true;
              return (
                <div key={sid} style={s.scheduleRow}>
                  <div style={s.scheduleTime}>{startT}–{endT}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={s.scheduleTitle}>{label}</div>
                    <div style={s.scheduleMeta}>
                      {formatScheduleDateRange(row)}{loopOn ? ' · Loop' : ''}
                    </div>
                  </div>
                </div>
              );
            })}
            {schedules.length > 8 && (
              <div style={s.emptyState}>+{schedules.length - 8} more</div>
            )}
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════
          TECHNICAL — Collapsed by default
          ═══════════════════════════════════════════════════════════ */}
      <TechnicalSection
        tv={tv}
        activeProfile={activeProfile}
        rotation={rotation}
        rotationLoading={rotationLoading}
        rotationSaving={rotationSaving}
        rotationError={rotationError}
        onSetRotation={handleSetRotation}
        profileError={profileError}
        onOpenProfilePicker={() => { setProfileError(null); setProfilePickerOpen(true); }}
        onDeviceUpdated={onDeviceUpdated}
        calibrationOpen={calibrationOpen}
        setCalibrationOpen={setCalibrationOpen}
        contentTitleByKey={contentTitleByKey}
      />

      <ProfilePickerModal
        open={profilePickerOpen}
        device={tv}
        reason={needsSetup ? 'first-run' : 'change'}
        onClose={() => setProfilePickerOpen(false)}
        onApply={handleApplyProfile}
        locations={knownLocations}
      />
    </div>
  );
};

/** Collapsible technical details — hidden by default for marketing users */
const TechnicalSection = ({
  tv, activeProfile, rotation, rotationLoading, rotationSaving, rotationError,
  onSetRotation, profileError, onOpenProfilePicker, onDeviceUpdated,
  calibrationOpen, setCalibrationOpen, contentTitleByKey = {},
}) => {
  const [open, setOpen] = useState(false);
  const s = detailStyles;

  return (
    <div style={s.techWrapper}>
      <button type="button" onClick={() => setOpen(!open)} style={s.techToggle}>
        <Settings size={14} />
        <span>Technical details</span>
        <ChevronDown size={14} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>

      {open && (
        <div style={s.techBody}>
          {/* Serial — physically printed on the device or via Settings → About → Serial */}
          {tv.serial && (
            <div style={s.techRow}>
              <span style={s.techLabel}>Serial</span>
              <span style={s.techValue}>{tv.serial}</span>
            </div>
          )}
          <div style={s.techRow}>
            <span style={s.techLabel}>System ID</span>
            <span style={s.techValue}>{tv.deviceId || tv.tvId || 'N/A'}</span>
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: -8, lineHeight: 1.4 }}>
            App-generated identifier. To verify on-site: open the CMS app → tap the version info area to reveal the device ID.
          </div>

          {/* EDID */}
          {tv.edidFingerprint?.width && (
            <div style={s.techRow}>
              <span style={s.techLabel}>HDMI / EDID</span>
              <span style={s.techValue}>
                {tv.edidFingerprint.width}×{tv.edidFingerprint.height}
                {tv.edidFingerprint.manufacturer ? ` · ${tv.edidFingerprint.manufacturer}` : ''}
                {tv.edidFingerprint.monitorName ? ` · "${tv.edidFingerprint.monitorName}"` : ''}
              </span>
            </div>
          )}

          {/* Playback health + the watchdog event journal used to render here.
              They moved to the internal Watchdog Console (see
              ~/sumadhura-watchdog-console): stall counts, decoder errors and
              self-heal restarts are engineering diagnostics, and this dashboard
              is a client-facing surface. The `health` snapshot still rides the
              device doc — nothing on the device or backend changed. */}

          {/* Display Health */}
          <DisplayHealthCard
            diagnostics={tv?.diagnostics}
            edidFingerprint={tv?.edidFingerprint}
            profileContent={tv?.profileContent}
          />

          {/* Diagnostics */}
          <DiagnosticsPanel diagnostics={tv?.diagnostics} device={tv} />

          {/* Calibration — disabled until TV app consumes values */}

          {profileError && (
            <div style={{ fontSize: 12, color: '#b91c1c', padding: '6px 8px', background: '#fef2f2', borderRadius: 4 }}>
              {profileError}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const detailStyles = {
  container: {
    padding: 0, backgroundColor: '#fff', borderRadius: 12,
    border: '1px solid #e2e8f0', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box',
    overflow: 'hidden',
  },
  // Hero
  hero: {
    padding: '20px 20px 16px', borderBottom: '1px solid #f1f5f9',
  },
  heroTop: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8,
  },
  heroName: {
    fontSize: 18, fontWeight: 700, color: '#0f172a', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 6,
    lineHeight: 1.3,
  },
  heroMeta: {
    display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8,
    fontSize: 12, color: '#64748b',
  },
  metaItem: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
  },
  statusDot: {
    width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
  },
  closeBtn: {
    all: 'unset', display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 28, height: 28, borderRadius: 6, color: '#94a3b8', cursor: 'pointer',
  },
  nameInput: {
    flex: 1, padding: '6px 10px', fontSize: 16, fontWeight: 600,
    border: '1px solid #c8a147', borderRadius: 6, fontFamily: 'inherit', outline: 'none',
  },
  nameBtn: {
    all: 'unset', display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 30, height: 30, borderRadius: 6, background: '#c8a147', color: '#fff', cursor: 'pointer',
  },
  nameCancelBtn: {
    all: 'unset', display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 30, height: 30, borderRadius: 6, border: '1px solid #e2e8f0', color: '#64748b', cursor: 'pointer',
  },
  nameError: { marginTop: 4, fontSize: 11, color: '#b91c1c' },
  profileBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10,
    padding: '4px 10px', borderRadius: 6,
    background: '#fffbeb', border: '1px solid #fde68a',
    fontSize: 12, fontWeight: 600, color: '#92400e',
  },
  profileDims: { color: '#94a3b8', fontWeight: 400 },
  profileChangeBtn: {
    all: 'unset', fontSize: 11, fontWeight: 600, color: '#c8a147', cursor: 'pointer',
    marginLeft: 4, textDecoration: 'underline',
  },
  // Sections
  section: { padding: '14px 20px', borderBottom: '1px solid #f1f5f9' },
  sectionLabel: {
    display: 'flex', alignItems: 'center', gap: 6,
    fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase',
    letterSpacing: '0.05em', marginBottom: 10,
  },
  emptyState: { fontSize: 13, color: '#94a3b8', fontStyle: 'italic' },
  // Now Playing
  nowPlayingCard: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 12px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0',
  },
  nowPlayingIcon: {
    width: 36, height: 36, borderRadius: 6, background: '#1e293b',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', flexShrink: 0,
  },
  nowPlayingTitle: { fontSize: 14, fontWeight: 600, color: '#0f172a' },
  nowPlayingMeta: { fontSize: 11, color: '#94a3b8', marginTop: 1 },
  // Schedule
  scheduleList: { display: 'flex', flexDirection: 'column', gap: 0 },
  scheduleRow: {
    display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0',
    borderBottom: '1px solid #f8fafc',
  },
  scheduleTime: {
    fontSize: 12, fontWeight: 700, color: '#c8a147', whiteSpace: 'nowrap',
    minWidth: 85, flexShrink: 0,
  },
  scheduleTitle: {
    fontSize: 13, fontWeight: 600, color: '#0f172a',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  scheduleMeta: { fontSize: 11, color: '#94a3b8', marginTop: 1 },
  // Technical
  techWrapper: { borderTop: '1px solid #f1f5f9' },
  techToggle: {
    all: 'unset', display: 'flex', alignItems: 'center', gap: 6, width: '100%',
    boxSizing: 'border-box', padding: '12px 20px',
    fontSize: 12, fontWeight: 600, color: '#64748b', cursor: 'pointer',
    textTransform: 'uppercase', letterSpacing: '0.05em',
  },
  techBody: {
    padding: '0 20px 16px', display: 'flex', flexDirection: 'column', gap: 12,
  },
  techRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
  },
  techLabel: { fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', flexShrink: 0 },
  techValue: {
    fontSize: 12, color: '#334155', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'right',
  },
  calibrateBtn: {
    all: 'unset', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    width: '100%', boxSizing: 'border-box', padding: '8px 0',
    background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 8,
    fontSize: 12, fontWeight: 600, color: '#64748b', cursor: 'pointer',
  },
};

export default TVDetails;
